import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp, ShoppingBag, Wallet, Clock, CheckCircle2, XCircle,
  ArrowRight, Package, LogOut, Shield, Tractor, AlertCircle,
  Receipt, Sparkles, Upload,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

import EmailVerificationBanner from '../components/EmailVerificationBanner';
import { DashboardSkeleton } from '../components/PageSkeletons';
import KpiCards from './dashboard/KpiCards';
import InvestmentsSection from './dashboard/InvestmentsSection';
import OrdersSection from './dashboard/OrdersSection';
import ActivityFeed from './dashboard/ActivityFeed';
import PayReceiptModal from './dashboard/PayReceiptModal';
import ReferralWallet from './dashboard/ReferralWallet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [receiptTarget, setReceiptTarget] = useState(null);   // investment doc pending "I've paid"

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/dashboard`);
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem('user_data');
        localStorage.removeItem('investor_data');
        navigate('/auth?redirect=/dashboard', { replace: true });
        return;
      }
      toast.error(err?.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const handleLogout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* soft */ }
    localStorage.removeItem('user_data');
    localStorage.removeItem('investor_data');
    toast.success('Signed out');
    navigate('/');
  };

  if (loading || !data) {
    return <DashboardSkeleton />;
  }

  const { profile, portfolio, kpis, investments, orders, activity, roles, referral } = data;
  const firstName = (profile.full_name || profile.email || '').split(' ')[0];

  return (
    <div className="min-h-screen bg-textured-cream page-enter" data-testid="unified-dashboard">
      {!data.is_email_verified && profile.email && (
        <EmailVerificationBanner email={profile.email} />
      )}

      {/* Hero header */}
      <div className="bg-canopy text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full opacity-20 blur-3xl"
             style={{ background: 'radial-gradient(circle, #fbbf24, transparent 70%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 pt-8 pb-24">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <p className="text-green-100/80 text-sm tracking-wide">Welcome back,</p>
              <h1 className="font-display text-4xl md:text-5xl font-semibold" data-testid="dashboard-welcome">
                {firstName} 👋
              </h1>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className="bg-white/20 hover:bg-white/25 border-none text-white">
                  {data.role === 'investor' ? 'Investor' : 'Customer'}
                </Badge>
                {roles.is_farmer && (
                  <Badge className="bg-amber-400 hover:bg-amber-400 text-amber-950 border-none">
                    <Tractor className="h-3 w-3 mr-1" /> Farmer
                  </Badge>
                )}
                {roles.is_farmer_pending && !roles.is_farmer && (
                  <Badge className="bg-amber-500/20 border-amber-300 text-amber-100">
                    Farmer application pending
                  </Badge>
                )}
                {data.is_email_verified && (
                  <Badge className="bg-green-500/25 border-none text-white">
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Verified
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Link to="/investment">
                <Button size="sm" className="bg-white text-green-700 hover:bg-green-50">
                  <TrendingUp className="h-4 w-4 mr-1" /> Invest
                </Button>
              </Link>
              <Link to="/shop">
                <Button size="sm" variant="secondary" className="bg-white/15 hover:bg-white/25 text-white border-white/30">
                  <ShoppingBag className="h-4 w-4 mr-1" /> Shop
                </Button>
              </Link>
              <Link to="/account/security">
                <Button size="sm" variant="ghost" className="text-white hover:bg-white/15" data-testid="dashboard-security-btn">
                  <Shield className="h-4 w-4 mr-1" /> Security
                </Button>
              </Link>
              <Button size="sm" variant="ghost" className="text-white hover:bg-white/15" onClick={handleLogout} data-testid="dashboard-logout-btn">
                <LogOut className="h-4 w-4 mr-1" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content — KPI cards straddle the hero. */}
      <div className="max-w-7xl mx-auto px-4 -mt-20 pb-12 space-y-6">
        <KpiCards portfolio={portfolio} kpis={kpis} />

        {/* Two-column layout: main content + right rail */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <InvestmentsSection
              investments={investments}
              onMarkPaid={(inv) => setReceiptTarget(inv)}
            />
            <OrdersSection orders={orders} />
          </div>

          <div className="space-y-6">
            {/* Farmer status widget — links to real Phase 3 pages. */}
            <Card>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Tractor className="h-4 w-4 text-amber-700" aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm">Farmers Market</p>
                    <p className="text-xs text-gray-500">
                      {roles.is_farmer
                        ? 'Post approved commodities'
                        : roles.is_farmer_pending
                        ? 'Application under review'
                        : 'Sell your produce nationwide'}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  {roles.is_farmer
                    ? 'You can list your produce for buyers across Nigeria.'
                    : roles.is_farmer_pending
                    ? 'We\'ll email you once your application is reviewed.'
                    : 'Apply to become a verified Daye Farm Ltd farmer.'}
                </p>
                <Link to={roles.is_farmer ? '/farmer/listings' : '/farmer/apply'}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    disabled={roles.is_farmer_pending && !roles.is_farmer}
                    data-testid="farmer-widget-cta"
                  >
                    {roles.is_farmer
                      ? 'Manage listings'
                      : roles.is_farmer_pending
                      ? 'Application pending'
                      : 'Apply now'}
                  </Button>
                </Link>
                <Link to="/farmers-market" className="block mt-2">
                  <Button variant="ghost" size="sm" className="w-full text-green-700 hover:bg-green-50">
                    Browse the market
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <ReferralWallet referral={referral || {}} onChanged={load} />

            <ActivityFeed activity={activity} />

            <Card>
              <CardContent className="p-5 space-y-2">
                <p className="font-semibold text-sm flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-green-600" aria-hidden="true" />
                  Quick actions
                </p>
                <Link to="/investment" className="block">
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <TrendingUp className="h-4 w-4 mr-2" /> Browse investment plans
                  </Button>
                </Link>
                <Link to="/shop" className="block">
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <Package className="h-4 w-4 mr-2" /> Shop fresh produce
                  </Button>
                </Link>
                <Link to="/track-order" className="block">
                  <Button variant="ghost" size="sm" className="w-full justify-start">
                    <Receipt className="h-4 w-4 mr-2" /> Track my order
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {receiptTarget && (
        <PayReceiptModal
          investment={receiptTarget}
          onClose={() => setReceiptTarget(null)}
          onSuccess={() => {
            setReceiptTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
